import { Popover } from '@headlessui/react';
import { AlertCircle, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { useOrganizationTimeZone } from '../../hooks/useOrganization';
import { useOttoNow } from '../../hooks/useOttoNow';
import { useWaitingSignal } from '../../hooks/useWaitingSignal';
import { ottoNowState, type OttoNowState } from '../../lib/bureau/ottoNow';
import { livenessOf } from '../../lib/workQueue/waitingSignal';
import { OttoNowFooter, OttoNowPanel } from './OttoNowPanel';

/** The pill's words for each state. */
function pillText(state: OttoNowState): { lead: string; rest: string | null } {
  switch (state.kind) {
    case 'doing':
      return { lead: state.headline.verb, rest: state.headline.about };
    case 'halted':
      return { lead: 'A calé', rest: state.title };
    case 'paused':
      return { lead: 'En pause', rest: state.next ? `reprend ${state.next}` : null };
  }
}

/**
 * The small Otto of the header (PLAN-BUREAU lot 4, asked on 07/10/2026: « qu'on puisse savoir ce
 * qu'il fait actuellement… pas besoin de préciser la partie technique »). In place of « Otto actif »
 * and its green dot: what he does now in one line — « Reprend la mission · Groupe Faure +2 » —,
 * breathing while he works, amber when he got stuck, grey when he rests until his next wake-up.
 * A click opens the detail (`OttoNowPanel`). Before any answer (or if the reader is down) it still
 * shows « Otto », so « Nouvelle mission » stays one click away; on a phone, only the round Otto. A
 * small amber dot says the Bureau is no longer sure to be up to date (the old « En direct » mark).
 */
export function OttoNow() {
  const data = useOttoNow();
  const timeZone = useOrganizationTimeZone();
  const signal = useWaitingSignal();
  // Time alone makes the Bureau stale (reads failing, nothing re-renders): look again every 5 s.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 5_000);
    return () => window.clearInterval(id);
  }, []);
  const stale = livenessOf(signal, Math.max(now, signal.checkedAt ?? 0)).kind === 'stale';
  const state: OttoNowState = data ? ottoNowState(data, new Date(), timeZone) : { kind: 'paused', next: null };
  const { lead, rest } = data ? pillText(state) : { lead: 'Otto', rest: null };
  const more = state.kind === 'doing' ? state.more : 0;

  return (
    <Popover className="relative">
      <Popover.Button
        title="Ce qu'Otto fait en ce moment"
        className={({ open }) =>
          twMerge(
            'relative mr-1 flex h-[34px] max-w-[400px] items-center gap-[9px] rounded-full py-0 pl-[5px] pr-[5px] text-[13px] ring-1 ring-inset transition focus:outline-none focus-visible:ring-2 focus-visible:ring-otto-500 md:pr-2.5',
            state.kind === 'doing' && 'bg-otto-50 text-desk-strong ring-otto-200 hover:bg-otto-100',
            state.kind === 'halted' && 'bg-amber-50 text-desk-strong ring-amber-200 hover:bg-amber-100',
            state.kind === 'paused' && 'bg-desk-canvas text-desk-soft ring-[#E4E7EC] hover:bg-desk-subtle',
            open && 'ring-2',
          )
        }
      >
        <span
          className={twMerge(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
            state.kind === 'doing' && 'animate-otto-live bg-otto-500 text-white motion-reduce:animate-none',
            state.kind === 'halted' && 'bg-amber-700 text-white',
            state.kind === 'paused' && 'bg-[#E4E7EC] text-desk-soft',
          )}
        >
          {state.kind === 'halted' ? <AlertCircle size={13} strokeWidth={2.4} /> : <Sparkles size={12} strokeWidth={2.2} />}
        </span>
        <span className="hidden min-w-0 truncate md:inline">
          <span className={twMerge('font-semibold', state.kind === 'paused' ? 'text-desk-strong' : 'text-desk-ink')}>{lead}</span>
          {rest && ` · ${rest}`}
        </span>
        {more > 0 && (
          <span className="hidden shrink-0 rounded-full bg-otto-100 px-1.5 text-[11.5px] font-bold leading-[18px] text-otto-900 md:inline">+{more}</span>
        )}
        {stale && <span className="absolute right-0.5 top-0.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white" aria-label="Bureau peut-être en retard" />}
      </Popover.Button>
      {/* On a phone the pill sits mid-header: the detail spans the screen rather than overflow it.
          Never taller than the screen: the list scrolls, the footer (talk, his screen) stays. */}
      <Popover.Panel className="fixed inset-x-2 top-14 z-40 flex max-h-[calc(100dvh-4.5rem)] flex-col rounded-2xl bg-white p-2.5 shadow-[0_24px_64px_-12px_rgba(16,24,40,0.28)] ring-1 ring-desk-ring md:absolute md:inset-x-auto md:right-0 md:top-auto md:mt-2 md:w-[420px] md:max-w-[calc(100vw-2rem)]">
        {({ close }) => (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {data ? (
                <OttoNowPanel data={data} timeZone={timeZone} onNavigate={() => close()} />
              ) : (
                <p className="mx-2 my-2 text-[13px] text-desk-muted">Je ne sais pas encore ce qu’Otto fait : la lecture n’a pas répondu.</p>
              )}
            </div>
            <OttoNowFooter onNavigate={() => close()} />
          </>
        )}
      </Popover.Panel>
    </Popover>
  );
}
